import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { useRegionContext } from '../../region/RegionContext';
import { queryAllRegions } from '../../utils/queryAllRegions';
import TabDescription from './TabDescription';
import 'leaflet/dist/leaflet.css';
import { Accessible, People } from '@mui/icons-material';

const UserMap = () => {
  const { db, isGlobal, allRegionDbs } = useRegionContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState(isGlobal ? 'country' : 'suburb');
  const [filter, setFilter] = useState('all'); // all, accessibility, regular
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionData, setRegionData] = useState([]);

  useEffect(() => {
    loadUsers();
  }, [db, isGlobal, allRegionDbs]);

  useEffect(() => {
    if (users.length > 0) {
      processRegionData();
    }
  }, [users, groupBy, filter]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      let data;
      if (isGlobal && allRegionDbs) {
        data = await queryAllRegions(allRegionDbs, null, (regionDb) =>
          regionDb.collection('Users').limit(2000)
        );
      } else {
        const snapshot = await db.collection('Users').limit(5000).get();
        data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const processRegionData = () => {
    let filteredUsers = users;

    // Apply filter
    if (filter === 'accessibility') {
      filteredUsers = users.filter(user => user.userDisabled === true);
    } else if (filter === 'regular') {
      filteredUsers = users.filter(user => !user.userDisabled);
    }

    const regionCounts = {};
    const regionCoords = {};
    const regionAccessibility = {};

    filteredUsers.forEach(user => {
      let region;
      let lat = user.userLat || user.latitude;
      let lon = user.userLon || user.longitude;

      if (groupBy === 'country') {
        region = user.userCountry || 'Australia';
        if (!lat || !lon) {
          lat = -25.2744;
          lon = 133.7751;
        }
      } else if (groupBy === 'state') {
        region = user.userState || 'Unknown';
        const stateCenters = {
          'VIC': { lat: -37.4713, lon: 144.7852 },
          'NSW': { lat: -33.8688, lon: 151.2093 },
          'QLD': { lat: -27.4698, lon: 153.0251 },
          'SA': { lat: -34.9285, lon: 138.6007 },
          'WA': { lat: -31.9505, lon: 115.8605 },
          'TAS': { lat: -42.8821, lon: 147.3272 },
          'ACT': { lat: -35.2809, lon: 149.1300 },
          'NT': { lat: -12.4634, lon: 130.8456 },
        };
        if (!lat || !lon) {
          const center = stateCenters[region] || stateCenters['VIC'];
          lat = center.lat;
          lon = center.lon;
        }
      } else if (groupBy === 'suburb') {
        region = user.userSuburb || 'Unknown';
      }

      if (!region) return;

      if (!regionCounts[region]) {
        regionCounts[region] = 0;
        regionCoords[region] = { lat: [], lon: [] };
        regionAccessibility[region] = 0;
      }
      regionCounts[region]++;

      if (user.userDisabled) {
        regionAccessibility[region]++;
      }

      if (lat && lon) {
        regionCoords[region].lat.push(lat);
        regionCoords[region].lon.push(lon);
      }
    });

    const processed = Object.entries(regionCounts).map(([name, count]) => {
      const coords = regionCoords[name];
      const avgLat = coords.lat.length > 0
        ? coords.lat.reduce((a, b) => a + b, 0) / coords.lat.length
        : -37.8136;
      const avgLon = coords.lon.length > 0
        ? coords.lon.reduce((a, b) => a + b, 0) / coords.lon.length
        : 144.9631;

      return {
        name,
        count,
        accessibilityCount: regionAccessibility[name] || 0,
        accessibilityPercentage: Math.round((regionAccessibility[name] / count) * 100),
        lat: avgLat,
        lon: avgLon,
      };
    });

    processed.sort((a, b) => b.count - a.count);

    setRegionData(processed);
  };

  const getColor = (count, maxCount, hasAccessibility) => {
    const intensity = count / maxCount;

    // Green color scheme for users
    if (hasAccessibility) {
      // Purple for accessibility users
      if (intensity > 0.7) return '#6B21A8';
      if (intensity > 0.5) return '#7C3AED';
      if (intensity > 0.3) return '#8B5CF6';
      if (intensity > 0.1) return '#A78BFA';
      return '#C4B5FD';
    } else {
      // Green for regular users
      if (intensity > 0.7) return '#15803D';
      if (intensity > 0.5) return '#16A34A';
      if (intensity > 0.3) return '#22C55E';
      if (intensity > 0.1) return '#4ADE80';
      return '#86EFAC';
    }
  };

  const maxCount = regionData.length > 0 ? Math.max(...regionData.map(r => r.count)) : 1;
  const totalUsers = users.length;
  const accessibilityUsers = users.filter(u => u.userDisabled).length;

  const MapUpdater = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
      map.setView(center, zoom);
    }, [center, zoom, map]);
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading user map...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const defaultCenter = [-25.2744, 133.7751];
  const defaultZoom = groupBy === 'country' ? 4 : groupBy === 'state' ? 5 : 10;

  return (
    <Box>
      <TabDescription>
        Geographic distribution of registered users from the Users collection, using userLat/userLon
        coordinates. Green markers represent regular users; purple markers highlight accessibility users
        (userDisabled flag). Use the filter to focus on accessibility users. Compare this map with the
        Venue Map to ensure venue supply matches user demand in each region.
      </TabDescription>
      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Group By</InputLabel>
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              label="Group By"
            >
              <MenuItem value="country">Country</MenuItem>
              <MenuItem value="state">State</MenuItem>
              <MenuItem value="suburb">Suburb</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(e, newFilter) => newFilter && setFilter(newFilter)}
            fullWidth
          >
            <ToggleButton value="all">
              <People sx={{ mr: 1 }} />
              All Users
            </ToggleButton>
            <ToggleButton value="accessibility">
              <Accessible sx={{ mr: 1 }} />
              Accessibility
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Total: ${totalUsers}`} color="success" />
            <Chip
              icon={<Accessible />}
              label={`${accessibilityUsers} (${Math.round((accessibilityUsers / totalUsers) * 100)}%)`}
              color="secondary"
            />
          </Box>
        </Grid>
      </Grid>

      {/* Map */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <MapContainer
              center={defaultCenter}
              zoom={defaultZoom}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapUpdater center={defaultCenter} zoom={defaultZoom} />

              {regionData.map((region) => (
                <CircleMarker
                  key={region.name}
                  center={[region.lat, region.lon]}
                  radius={Math.sqrt(region.count) * 3}
                  pathOptions={{
                    color: getColor(region.count, maxCount, region.accessibilityCount > region.count / 2),
                    fillColor: getColor(region.count, maxCount, region.accessibilityCount > region.count / 2),
                    fillOpacity: 0.6,
                    weight: 2,
                  }}
                  eventHandlers={{
                    click: () => setSelectedRegion(region),
                  }}
                >
                  <Tooltip permanent={region.count > maxCount * 0.3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {region.name}
                      </Typography>
                      <Typography variant="body2">
                        {region.count} user{region.count !== 1 ? 's' : ''}
                      </Typography>
                      {region.accessibilityCount > 0 && (
                        <Typography variant="caption" color="secondary">
                          <Accessible sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                          {region.accessibilityCount} ({region.accessibilityPercentage}%)
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Heat Map Legend
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Regular Users (Green)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {['#86EFAC', '#4ADE80', '#22C55E', '#16A34A', '#15803D'].map((color, idx) => (
                  <Box key={color} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: color,
                      }}
                    />
                    <Typography variant="caption">
                      {idx === 4 ? 'Highest' : idx === 0 ? 'Lowest' : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Accessibility Users (Purple)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {['#C4B5FD', '#A78BFA', '#8B5CF6', '#7C3AED', '#6B21A8'].map((color, idx) => (
                  <Box key={color} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: color,
                      }}
                    />
                    <Typography variant="caption">
                      {idx === 4 ? 'Highest' : idx === 0 ? 'Lowest' : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            💡 Tip: Purple circles indicate regions where >50% of users have accessibility needs. Darker = more users.
          </Typography>
        </CardContent>
      </Card>

      {/* Top Regions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Top 10 User Regions
          </Typography>
          <Grid container spacing={2}>
            {regionData.slice(0, 10).map((region, index) => (
              <Grid item xs={12} sm={6} md={4} key={region.name}>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: selectedRegion?.name === region.name ? 'grey.100' : 'white',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                  onClick={() => setSelectedRegion(region)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {index + 1}. {region.name}
                    </Typography>
                    <Chip
                      label={region.count}
                      size="small"
                      sx={{
                        bgcolor: getColor(region.count, maxCount, false),
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  {region.accessibilityCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      <Accessible sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                      {region.accessibilityCount} with accessibility needs ({region.accessibilityPercentage}%)
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserMap;
